/**
 * useConfirm — app-wide imperative themed confirmation.
 *
 * Closes the "no shared confirm primitive" gap that kept letting native
 * `window.confirm()` creep back in (it was less code than wiring up a
 * `<ConfirmDialog>` + state at every call site, and a couple of surfaces
 * even hand-rolled their own local version). Mount `<ConfirmProvider>`
 * once at the root, then any component does:
 *
 *   const confirm = useConfirm()
 *   if (await confirm({ title, description, confirmLabel, destructive })) {
 *     // user confirmed
 *   }
 *
 * It renders the existing themed `ConfirmDialog`, so the modal shell,
 * dismiss-guarding, and styling are shared — not reinvented per site.
 * The promise resolves `true` on confirm, `false` on cancel/dismiss.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { ConfirmDialog } from '@/components/ConfirmDialog'

export interface ConfirmOptions {
  title: string
  description?: ReactNode
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string
  /** Red destructive confirm button. Defaults to `true` — most confirms
   * gate a delete/remove. Pass `false` for a neutral confirm. */
  destructive?: boolean
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm {
  opts: ConfirmOptions
  resolve: (value: boolean) => void
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const confirm = useCallback<ConfirmFn>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        setPending((prev) => {
          // Supersede an unanswered confirm rather than orphan its promise
          // (double-click / concurrent trigger) — resolve the prior one
          // `false` so its caller doesn't hang forever (Copilot #1146).
          prev?.resolve(false)
          return { opts, resolve }
        })
      }),
    [],
  )

  // `pending` is non-null whenever the dialog is rendered, so reading it
  // from the closure here resolves the right promise without a setState
  // updater side-effect.
  function settle(value: boolean) {
    pending?.resolve(value)
    setPending(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialog
          open
          title={pending.opts.title}
          description={pending.opts.description ?? ''}
          confirmLabel={pending.opts.confirmLabel ?? 'Confirm'}
          confirmVariant={pending.opts.destructive === false ? 'default' : 'destructive'}
          onClose={() => settle(false)}
          onConfirm={() => settle(true)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within a <ConfirmProvider>')
  return ctx
}
